import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ListChecks, Trash, CaretDown, CaretUp } from '@phosphor-icons/react';

const API = `${window.location.origin}/api`;

function TemplatePickerModal({ taskId, onClose, onApplied }) {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [applying, setApplying] = useState(null);
  const [expanded, setExpanded] = useState(null);

  useEffect(() => {
    fetchTemplates();
  }, []);

  const fetchTemplates = async () => {
    try {
      const res = await fetch(`${API}/checklist-templates`, { credentials: 'include' });
      if (res.ok) setTemplates(await res.json());
    } catch (err) {
      console.error('Failed to fetch templates:', err);
    } finally {
      setLoading(false);
    }
  };

  const applyTemplate = async (templateId) => {
    setApplying(templateId);
    try {
      const res = await fetch(`${API}/tasks/${taskId}/apply-template/${templateId}`, {
        method: 'POST',
        credentials: 'include',
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || 'Failed to apply');
      }
      const data = await res.json();
      if (onApplied) onApplied(data);
      onClose();
    } catch (err) {
      alert(err.message);
    } finally {
      setApplying(null);
    }
  };

  const deleteTemplate = async (templateId, e) => {
    e.stopPropagation();
    if (!window.confirm('Delete this template? This cannot be undone.')) return;
    try {
      await fetch(`${API}/checklist-templates/${templateId}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      setTemplates(templates.filter(t => t.template_id !== templateId));
    } catch (err) {
      console.error('Failed to delete template:', err);
    }
  };

  return (
    <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm flex items-center justify-center p-4 z-[60]" onClick={onClose}>
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        onClick={(e) => e.stopPropagation()}
        className="bg-white rounded-lg shadow-lg max-w-md w-full max-h-[80vh] flex flex-col"
        data-testid="template-picker-modal"
      >
        <div className="border-b border-slate-200 p-5 flex items-center justify-between flex-shrink-0">
          <h2 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
            <ListChecks size={20} weight="duotone" className="text-navy" />
            Checklist Templates
          </h2>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600" data-testid="close-template-picker">
            <X size={22} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-5">
          {loading ? (
            <div className="flex justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-4 border-navy border-t-transparent" />
            </div>
          ) : templates.length === 0 ? (
            <div className="text-center py-8" data-testid="no-templates">
              <p className="text-sm text-slate-500 mb-1">No templates yet</p>
              <p className="text-xs text-slate-400">Save a task's sub-tasks as a template to reuse it later.</p>
            </div>
          ) : (
            <div className="space-y-2">
              {templates.map((tmpl) => (
                <div
                  key={tmpl.template_id}
                  className="border border-slate-200 rounded-lg overflow-hidden hover:border-blue-200 transition-colors"
                  data-testid={`template-${tmpl.template_id}`}
                >
                  <div
                    className="flex items-center justify-between p-3 cursor-pointer"
                    onClick={() => setExpanded(expanded === tmpl.template_id ? null : tmpl.template_id)}
                  >
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-slate-900 truncate">{tmpl.name}</p>
                      <p className="text-xs text-slate-400">{tmpl.items.length} items{tmpl.description ? ` \u00b7 ${tmpl.description}` : ''}</p>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0 ml-2">
                      <button
                        onClick={(e) => deleteTemplate(tmpl.template_id, e)}
                        className="text-slate-300 hover:text-red-500 transition-colors p-1"
                        data-testid={`delete-template-${tmpl.template_id}`}
                      >
                        <Trash size={14} />
                      </button>
                      {expanded === tmpl.template_id ? (
                        <CaretUp size={14} className="text-slate-400" />
                      ) : (
                        <CaretDown size={14} className="text-slate-400" />
                      )}
                    </div>
                  </div>

                  <AnimatePresence>
                    {expanded === tmpl.template_id && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        className="overflow-hidden"
                      >
                        <div className="px-3 pb-3 border-t border-slate-100 pt-2">
                          <ul className="space-y-1 mb-3">
                            {tmpl.items.map((item, i) => (
                              <li key={i} className="text-xs text-slate-600 flex items-center gap-1.5">
                                <span className="w-1 h-1 rounded-full bg-slate-400 flex-shrink-0" />
                                {item}
                              </li>
                            ))}
                          </ul>
                          <button
                            onClick={() => applyTemplate(tmpl.template_id)}
                            disabled={applying === tmpl.template_id}
                            className="w-full bg-navy text-white hover:bg-navy-hover transition-colors font-medium px-3 py-2 rounded-lg text-xs disabled:opacity-50 flex items-center justify-center gap-1.5"
                            data-testid={`apply-template-${tmpl.template_id}`}
                          >
                            {applying === tmpl.template_id ? 'Applying...' : 'Apply to Task'}
                          </button>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              ))}
            </div>
          )}
        </div>
      </motion.div>
    </div>
  );
}

export default TemplatePickerModal;
